import { useT } from "@/i18n";
import { useQuarterPreview } from "@/hooks/useQuarterPreview";
import { formatDate, formatQuarter } from "@/lib/format";
import { DatePicker } from "@/components/ui/DatePicker";
import { InlineError, InlineSpinner, useInlineEdit, ViewSurface } from "./shared";

/**
 * A business date, edited in place, showing the quarter it implies.
 *
 * **This is the only way an open opportunity changes quarter.** There is no "move to Q3"
 * control anywhere in the detail, because the quarter is derived (from this date) and a
 * picker for a derived value would be a second, editable copy of it. The old Move dialog's
 * quarter picker asked for a date anyway; this starts at the truth.
 *
 * The quarter shown — before the commit and after it — is **the server's answer**, from the
 * same `extract()` the generated columns use. Nothing here computes a quarter from a month.
 */
export function InlineDate({
  label,
  value,
  editable,
  disabled,
  /** The derived quarter and year, as the record currently carries them. */
  quarter,
  year,
  onCommit,
}: {
  label: string;
  /** `yyyy-MM-dd`. */
  value: string;
  editable: boolean;
  disabled?: boolean;
  quarter: number;
  year: number;
  onCommit: (next: string) => Promise<unknown>;
}) {
  const t = useT();
  const field = useInlineEdit<string>({ value, equals: (a, b) => a === b, onCommit });
  // Hook order is fixed: the preview is asked for whatever the draft holds, and answers
  // nothing at all until that is a complete date.
  const preview = useQuarterPreview(field.editing ? field.draft : "");

  if (!field.editing) {
    return (
      <div className="min-w-0">
        <ViewSurface
          label={label}
          editable={editable}
          disabled={disabled}
          saving={field.saving}
          onActivate={field.begin}
        >
          {formatDate(value)}
        </ViewSurface>
        {/* The derivation on a second row rather than trailing the date (2026-08-25): beside
            it, the bracketed quarter wrapped the value onto two lines in a four-column hero.
            Underneath, it lines up with the stage's age — one place a fact's derived
            companion lives, on every column that has one. Still rendered visibly *as*
            derived, which is the book's acceptance criterion. */}
        <p className="text-muted-foreground mt-0.5 text-xs">{formatQuarter(quarter, year)}</p>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        {/* The picker opens with the field, since clicking the value *is* the request to
            change it — one gesture, not two. Choosing a day commits; closing it without
            choosing leaves the value alone, which is what Escape has always done here. */}
        <DatePicker
          label={label}
          value={field.draft}
          disabled={field.saving}
          // Clicking the value *is* the request to change it, so the picker opens with the
          // field rather than making the reader click twice. The rule this suppresses is about
          // focus stealing on page load; here the reader's own click is what opened it.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onChange={(next: string) => {
            field.setDraft(next);
            void field.commit(next);
          }}
          onClose={field.cancel}
        />
        {field.saving && <InlineSpinner />}
      </div>

      {preview.data && (
        <p className="text-muted-foreground mt-0.5 text-xs">
          {t("move.derivedPrefix")}{" "}
          <span className="text-foreground font-medium">
            {formatQuarter(preview.data.quarter, preview.data.year)}
          </span>
          {t("move.derivedSuffix")}
        </p>
      )}
      {field.error && <InlineError message={field.error} />}
    </div>
  );
}
